import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode, useRef } from 'react'
import type {
	PanelNowView,
	PanelRowView,
	PanelStatus,
	PanelView,
} from '../model/console-view'
import { useKeepCurrentInView } from '../use-keep-current-in-view'
import { ActivityChips, ActivityLine } from './activity-chips'

const STATUS_KEYS: Record<PanelStatus, string> = {
	opening: 'in_person.presenter.status.opening',
	windowed: 'in_person.presenter.status.windowed',
	fullscreen: 'in_person.presenter.status.fullscreen',
	black: 'in_person.presenter.status.black',
	lost: 'in_person.presenter.status.lost',
	'other-build': 'in_person.presenter.reload',
}

const TONES: Record<PanelStatus, 'ok' | 'wait' | 'bad'> = {
	opening: 'wait',
	windowed: 'wait',
	fullscreen: 'ok',
	black: 'wait',
	lost: 'bad',
	'other-build': 'bad',
}

/**
 * A column with a fixed head (status, clock, now) and foot (actions); the
 * agenda between them takes the rest and scrolls on its own.
 */
const Panel = styled.aside`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 320px;
	flex: none;
	min-height: 0;
	padding: 16px 20px 20px;
	overflow: hidden;
	background: ${colors.white};
	border-left: 1px solid ${colors.borderGray};

	${ui.responsive.desktop1440} {
		width: 360px;
	}
`

/** The status and the settings gear on one row; the settings box opens under it. */
const Head = styled.div`
	position: relative;
	display: flex;
	align-items: stretch;
	gap: 8px;
`

const Status = styled.div<{ tone: 'ok' | 'wait' | 'bad' }>`
	display: flex;
	flex: 1;
	min-width: 0;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	border-radius: 10px;
	background: ${colors.paleGray};
	font-weight: 600;
	font-size: 14px;
	color: ${colors.darkIndigo};

	::before {
		content: '';
		flex: none;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background: ${({ tone }) =>
			tone === 'ok'
				? colors.greenishCyan
				: tone === 'bad'
				? colors.red
				: colors.heather};
	}
`

const Clock = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`

const Time = styled.div`
	font-size: 32px;
	font-weight: 700;
	color: ${colors.darkIndigo};
`

const Small = styled.div`
	font-size: 13px;
	color: ${colors.heather};
`

const Block = styled.section`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: none;
`

const Label = styled.h3`
	margin: 0;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${colors.heather};
`

const Title = styled.div`
	font-size: 17px;
	font-weight: 700;
	color: ${colors.darkIndigo};
`

const Plan = styled.div<{ over: boolean }>`
	font-size: 14px;
	color: ${({ over }) => (over ? colors.red : colors.darkIndigo)};
	font-weight: ${({ over }) => (over ? 700 : 400)};
`

const Notes = styled.div`
	max-height: 96px;
	overflow-y: auto;
	font-size: 14px;
	line-height: 1.45;
	white-space: pre-wrap;
	color: ${colors.darkIndigo};
`

const AgendaBlock = styled.section`
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex: 1;
	min-height: 0;
`

const AgendaScroll = styled.ol`
	position: relative;
	flex: 1;
	min-height: 0;
	margin: 0 -8px;
	padding: 0 8px;
	list-style: none;
	overflow-y: auto;
`

const Day = styled.li`
	padding: 10px 0 4px;
	font-size: 12px;
	font-weight: 700;
	color: ${colors.heather};
`

const Row = styled.li<{ position: PanelRowView['position'] }>`
	display: flex;
	gap: 10px;
	padding: 8px 10px;
	margin-bottom: 4px;
	border-radius: 10px;
	background: ${({ position }) =>
		position === 'current' ? colors.paleGray : 'transparent'};
	box-shadow: ${({ position }) =>
		position === 'current' ? `inset 3px 0 0 ${colors.indigoBlue}` : 'none'};
	opacity: ${({ position }) => (position === 'done' ? 0.5 : 1)};
`

const RowTime = styled.div`
	flex: none;
	width: 40px;
	padding-top: 1px;
	font-size: 13px;
	font-variant-numeric: tabular-nums;
	color: ${colors.heather};
`

const RowBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`

const RowTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: ${colors.darkIndigo};
	overflow-wrap: anywhere;
`

const Actions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	flex: none;
`

type Props = {
	view: PanelView
	/** The settings gear, beside the status. */
	settings: ReactNode
	blank: boolean
	showMaximise: boolean
	onToggleBlank: () => void
	onEnd: () => void
}

const useTimeText = (now: PanelNowView): string | undefined => {
	const translate = intl.useTranslate()
	const { minutesLeft } = now
	if (minutesLeft === undefined) {
		return undefined
	}
	return minutesLeft >= 0
		? translate('in_person.presenter.time_left', { minutes: minutesLeft })
		: translate('in_person.presenter.time_over', { minutes: -minutesLeft })
}

const NowStep: FC<{ now: PanelNowView }> = ({ now }) => {
	const timeText = useTimeText(now)
	return (
		<>
			<Title>{now.title}</Title>
			<Plan over={now.over}>
				{now.plan}
				{timeText ? ` · ${timeText}` : ''}
			</Plan>
			{now.activity ? <ActivityLine activity={now.activity} /> : null}
			{now.notes ? (
				<Notes data-testid="presenter.panel.notes">{now.notes}</Notes>
			) : null}
		</>
	)
}

/**
 * The console's side column while presenting: what the projector shows, the
 * clock, the agenda step against its plan with the trainer's notes, the whole
 * agenda to scroll through, black screen and end - and, beside the status,
 * the gear that opens the settings.
 */
export const PresenterPanel: FC<Props> = ({
	view: { status, screenLabel, clock, elapsed, now, agenda },
	settings,
	blank,
	showMaximise,
	onToggleBlank,
	onEnd,
}) => {
	const list = useRef<HTMLOListElement>(null)
	useKeepCurrentInView(
		list,
		agenda.findIndex(row => row.position === 'current')
	)

	return (
		<Panel data-testid="presenter.panel" aria-label="Presenter">
			<Head>
				<Status
					role="status"
					data-testid="presenter.panel.status"
					data-status={status}
					tone={TONES[status]}
				>
					<span>
						<intl.Translate>{STATUS_KEYS[status]}</intl.Translate>
						{screenLabel ? ` · ${screenLabel}` : ''}
					</span>
				</Status>
				{settings}
			</Head>

			<Clock>
				<Time data-testid="presenter.panel.clock">{clock}</Time>
				<Small>
					<intl.Translate time={elapsed}>
						in_person.presenter.elapsed
					</intl.Translate>
				</Small>
			</Clock>

			<Block data-testid="presenter.panel.now">
				<Label>
					<intl.Translate>in_person.presenter.now</intl.Translate>
				</Label>
				{now ? (
					<NowStep now={now} />
				) : (
					<Small>
						<intl.Translate>in_person.presenter.not_started</intl.Translate>
					</Small>
				)}
			</Block>

			<AgendaBlock data-testid="presenter.panel.agenda">
				<Label>
					<intl.Translate>in_person.presenter.agenda</intl.Translate>
				</Label>
				<AgendaScroll ref={list} data-testid="presenter.panel.agenda.list">
					{agenda.map(row => (
						<FragmentRow key={row.key} row={row} />
					))}
				</AgendaScroll>
			</AgendaBlock>

			<Actions>
				<ui.ButtonMinor
					small
					onClick={onToggleBlank}
					data-testid="presenter.black"
					aria-pressed={blank}
				>
					<intl.Translate>
						{blank
							? 'in_person.presenter.show_screen'
							: 'in_person.presenter.black_screen'}
					</intl.Translate>
					{' (B)'}
				</ui.ButtonMinor>
				<ui.ButtonPrimary
					small
					onClick={onEnd}
					data-testid="presenter.panel.end"
				>
					<intl.Translate>in_person.presenter.end</intl.Translate>
				</ui.ButtonPrimary>
				{showMaximise ? (
					<Small>
						<intl.Translate>in_person.presenter.maximise</intl.Translate>
					</Small>
				) : null}
			</Actions>
		</Panel>
	)
}

const FragmentRow: FC<{ row: PanelRowView }> = ({ row }) => (
	<>
		{row.day ? <Day data-agenda-day>{row.day}</Day> : null}
		<Row
			data-agenda-item
			position={row.position}
			data-position={row.position}
			aria-current={row.position === 'current' ? 'step' : undefined}
		>
			<RowTime>{row.at}</RowTime>
			<RowBody>
				<RowTitle>{row.title}</RowTitle>
				<ActivityChips groups={row.groups} />
			</RowBody>
		</Row>
	</>
)
